import { useMemo, useState } from "react";
import { Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Panel } from "@/components/KpiCard";
import { useFilters } from "@/context/FilterContext";
import { api, errMsg, moisLabel } from "@/lib/api";

const PORTEES = { mois: "Un mois", annee: "Une année", tout: "Toutes mes données" };

const libelle = (b) => (b.portee === "mois" ? `de ${moisLabel(b.mois)}` : b.portee === "annee" ? `de l'année ${b.annee}` : "de toutes les périodes");

export const EffacerPanel = () => {
  const { options, refreshOptions } = useFilters();
  const annees = useMemo(() => [...new Set(options.mois.map((m) => m.slice(0, 4)))], [options.mois]);
  const [b, setB] = useState({ portee: "mois", mois: "", annee: "" });
  const [apercu, setApercu] = useState(null);
  const [conf, setConf] = useState("");
  const [busy, setBusy] = useState(false);
  const body = { portee: b.portee, mois: b.portee === "mois" ? b.mois : null, annee: b.portee === "annee" ? Number(b.annee) : null };
  const pret = b.portee === "tout" || (b.portee === "mois" && b.mois) || (b.portee === "annee" && b.annee);

  const ouvrir = async () => {
    try { const { data } = await api.post("/donnees/apercu", body); setConf(""); setApercu(data); } catch (e) { toast.error(errMsg(e)); }
  };
  const effacer = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/donnees/effacer", { ...body, confirmation: conf });
      localStorage.removeItem("rb_dossier_faits");
      toast.success(`Effacé : ${data.operations} opération(s) bancaire(s) et ${data.reservations} réservation(s)`);
      setApercu(null); setB({ portee: b.portee, mois: "", annee: "" }); refreshOptions();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Panel title="Effacer mes données" testId="effacer-panel">
      <p className="text-xs text-slate-500 mb-4">Supprime les opérations bancaires, réservations, rapprochements, factures Booking et notes de la période choisie. Vos paramètres et règles d'affectation sont conservés. Action définitive.</p>
      <div className="flex flex-col sm:flex-row gap-2">
        <Select value={b.portee} onValueChange={(portee) => setB({ ...b, portee })}>
          <SelectTrigger className="h-9 sm:w-52" data-testid="effacer-portee-select"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(PORTEES).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`effacer-portee-${k}`}>{v}</SelectItem>)}</SelectContent>
        </Select>
        {b.portee === "mois" && (
          <Select value={b.mois} onValueChange={(mois) => setB({ ...b, mois })}>
            <SelectTrigger className="h-9 sm:w-48" data-testid="effacer-mois-select"><SelectValue placeholder="Choisir le mois" /></SelectTrigger>
            <SelectContent>{options.mois.map((m) => <SelectItem key={m} value={m} data-testid={`effacer-mois-${m}`}>{moisLabel(m)}</SelectItem>)}</SelectContent>
          </Select>
        )}
        {b.portee === "annee" && (
          <Select value={b.annee} onValueChange={(annee) => setB({ ...b, annee })}>
            <SelectTrigger className="h-9 sm:w-40" data-testid="effacer-annee-select"><SelectValue placeholder="Choisir l'année" /></SelectTrigger>
            <SelectContent>{annees.map((a) => <SelectItem key={a} value={a} data-testid={`effacer-annee-${a}`}>{a}</SelectItem>)}</SelectContent>
          </Select>
        )}
        <Button variant="outline" disabled={!pret} onClick={ouvrir} className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800" data-testid="effacer-open-button">
          <Trash2 className="h-4 w-4 mr-2" />Effacer…
        </Button>
      </div>
      <AlertDialog open={!!apercu} onOpenChange={(o) => !o && setApercu(null)}>
        <AlertDialogContent data-testid="effacer-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Effacer les données {libelle(b)} ?</AlertDialogTitle>
            <AlertDialogDescription data-testid="effacer-apercu">
              {apercu && `${apercu.operations} opération(s) bancaire(s) et ${apercu.reservations} réservation(s) seront supprimées définitivement, avec leurs rapprochements et notes.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input value={conf} onChange={(e) => setConf(e.target.value)} placeholder="Tapez EFFACER pour confirmer" data-testid="effacer-confirm-input" />
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="effacer-cancel-button">Annuler</AlertDialogCancel>
            <Button onClick={effacer} disabled={busy || conf.trim().toUpperCase() !== "EFFACER"} className="bg-red-600 hover:bg-red-700" data-testid="effacer-confirm-button">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Effacer définitivement
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Panel>
  );
};
